import React from "react";
import {
  HiDevicePhoneMobile,
  HiComputerDesktop,
  HiGlobeAlt,
  HiXMark,
  HiBellSlash,
  HiPlus,
} from "react-icons/hi2";
import { useFCMTokenManagement } from "../hooks/useReminder";
import { FCMTokenData } from "../types/user";
import { formatRelativeTime, type DateInput } from "../utils/dateUtils";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../utils/notificationUtils";
import { notificationService } from "../services/notificationService";

interface FCMTokenManagerProps {
  className?: string;
  transparent?: boolean; // New prop for transparent/embedded mode
}

const FCMTokenManager: React.FC<FCMTokenManagerProps> = ({
  className = "",
  transparent = false,
}) => {
  const {
    tokens,
    loading,
    error,
    refreshTokens,
    toggleDeviceNotifications,
    addCurrentDevice,
    removeInvalidToken,
  } = useFCMTokenManagement();

  // Get current device ID to identify which device is the current one
  const currentDeviceId = notificationService.getCurrentDeviceId();

  const getDeviceIcon = (platform?: string) => {
    switch (platform) {
      case "mobile":
        return <HiDevicePhoneMobile className="w-5 h-5" />;
      case "desktop":
        return <HiComputerDesktop className="w-5 h-5" />;
      default:
        return <HiGlobeAlt className="w-5 h-5" />;
    }
  };

  const formatLastUsed = (lastUsed: DateInput): string => {
    return formatRelativeTime(lastUsed);
  };

  const handleToggleDevice = async (
    deviceId: string,
    currentEnabled: boolean
  ) => {
    try {
      await toggleDeviceNotifications(deviceId, !currentEnabled);
      showSuccessNotification(
        `Notifications ${!currentEnabled ? "enabled" : "disabled"}`,
        `Successfully ${
          !currentEnabled ? "enabled" : "disabled"
        } notifications for this device`
      );
    } catch (err) {
      console.error("Failed to toggle device notifications:", err);
      showErrorNotification(
        "Failed to update notifications",
        err instanceof Error
          ? err.message
          : "Unable to change notification settings. Please try again."
      );
    }
  };

  const handleAddCurrentDevice = async () => {
    try {
      await addCurrentDevice();
      showSuccessNotification(
        "Device added successfully",
        "This device has been added to your notification list"
      );
    } catch (err) {
      console.error("Failed to add current device:", err);
      showErrorNotification(
        "Failed to add device",
        err instanceof Error
          ? err.message
          : "Unable to add this device for notifications. Please try again."
      );
    }
  };

  const handleRemoveToken = async (token: string) => {
    if (
      window.confirm(
        "Are you sure you want to remove this device? You will stop receiving notifications on this device."
      )
    ) {
      try {
        await removeInvalidToken(token);
        showSuccessNotification(
          "Device removed",
          "Device has been removed from your notification list"
        );
      } catch (err) {
        console.error("Failed to remove token:", err);
        showErrorNotification(
          "Failed to remove device",
          err instanceof Error
            ? err.message
            : "Unable to remove this device. Please try again."
        );
      }
    }
  };

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <h2 className="text-lg font-semibold text-base-content">
            Registered Devices
          </h2>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-base-content/80 mb-4">
        Manage which devices receive push notifications. You can enable or
        disable notifications for each device individually.
      </p>

      {/* Error Display */}
      {error && (
        <div
          className={`alert alert-error mb-4 ${
            transparent ? "bg-red-500/10/60 border-red-200/60 backdrop-blur-sm" : ""
          }`}
        >
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && tokens.length === 0 && (
        <div className="flex justify-center py-8">
          <span className="loading loading-spinner loading-md"></span>
        </div>
      )}

      {/* No Devices */}
      {!loading && tokens.length === 0 && (
        <div className="text-center py-8">
          <HiBellSlash className="w-12 h-12 text-base-content/50 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-base-content/80 mb-2">
            No Devices Registered
          </h3>
          <p className="text-sm text-base-content/60 mb-4">
            Add this device to start receiving push notifications.
          </p>
          <button
            onClick={handleAddCurrentDevice}
            className="btn !bg-base-200 hover:!bg-base-300"
            disabled={loading}
          >
            <HiPlus className="w-4 h-4 mr-2" />
            Add This Device
          </button>
        </div>
      )}

      {/* Device List */}
      {tokens.length > 0 && (
        <div className="space-y-3">
          {tokens.map((tokenData: FCMTokenData) => (
            <div
              key={tokenData.deviceId || tokenData.token}
              className={`pt-4 pb-4 ${
                tokenData.enabled
                  ? transparent
                    ? " bg-base-200 rounded-md px-4"
                    : " bg-base-200 rounded-md px-4"
                  : transparent
                  ? "border-base-200/60 bg-base-100/60 backdrop-blur-sm"
                  : "border-base-200 bg-base-100"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div>
                    {tokenData.deviceId === currentDeviceId && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-base-300 text-base-content">
                        This Device
                      </span>
                    )}
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-base-content">
                        {tokenData.deviceName || "Unknown Device"}
                      </h4>
                    </div>
                    <div className="text-sm text-base-content/60">
                      <p>
                        Platform:{" "}
                        <strong>{tokenData.platform || "Unknown"}</strong>
                      </p>
                      <p>
                        Last used:{" "}
                        <strong>{formatLastUsed(tokenData.lastUsed)}</strong>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {/* Remove Device */}
                  <button
                    onClick={() => handleRemoveToken(tokenData.token)}
                    className=" rounded-full p-2"
                    disabled={loading}
                    title="Remove this device"
                  >
                    <HiXMark className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Token Info (for debugging - can be removed in production) */}
              {process.env.NODE_ENV === "development" && (
                <div className="mt-2 text-xs text-base-content/50 font-mono">
                  Token: {tokenData.token.substring(0, 20)}...
                </div>
              )}

              <div className="mt-4">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={tokenData.enabled}
                    onChange={() =>
                      handleToggleDevice(
                        tokenData.deviceId || tokenData.token,
                        tokenData.enabled
                      )
                    }
                    disabled={loading}
                  />
                  <label className="text-[14px]">
                    Enable notifications for this device
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Refresh Button */}
      {tokens.length > 0 && (
        <div className="mt-8 mb-3 space-y-3 text-sm">
          <button
            onClick={refreshTokens}
            className="px-4 py-2 rounded-md border border-1 w-full"
            disabled={loading}
          >
            {loading ? (
              <span className="loading loading-spinner loading-sm mr-2"></span>
            ) : null}
            Refresh Devices
          </button>

          {/* Debug button for development */}
          {process.env.NODE_ENV === "development" && (
            <button
              onClick={() => notificationService.debugDeviceInfo()}
              className="px-4 py-2 rounded-md border border-1 w-full"
              disabled={loading}
            >
              Debug Device Info
            </button>
          )}
        </div>
      )}

      <button
        onClick={handleAddCurrentDevice}
        className="flex p-2 bg-neutral justify-center rounded-md text-white items-center font-grotesk px-4 w-full"
        disabled={loading}
      >
        <HiPlus className="w-4 h-4 mr-1" />
        Add This Device
      </button>

      {/* Info Text */}
      <p className="text-xs text-base-content/60 mt-4">
        Devices are automatically added when you grant notification permissions.
        You can manage notifications for each device independently.
      </p>
    </div>
  );
};

export default FCMTokenManager;
